import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AssigneeRef, SubmissionColumn, SubmissionRow, SubmissionView, TRIAGE_STATUSES, TriagePatch, TriageStatus,
  bulkDeleteSubmissionsApi, bulkTriageApi, createViewApi, deleteSubmissionApi,
  deleteViewApi, getSubmissionColumns, listAssigneesApi, listFormConnections, listViewsApi,
  querySubmissionsApi, saveSubmissionColumns, submissionQueryString, updateViewApi,
} from '../../api/forms';
import { adminMe } from '../../api/admin';
import { avatarIntent, initialsOf } from '../../api/access';
import type { DeliveryFilter, FilterCondition, SubmissionFilters, ViewConfig } from '../../shared/types';
import { useFormWorkspace } from './FormWorkspace';
import { ColumnsPanel, Popover, ViewsPanel } from './SubmissionPopovers';
import FilterPanel, { conditionComplete, opLabel } from './FilterPanel';
import SubmissionDrawer from './SubmissionDrawer';
import ResendDialog from './ResendDialog';
import FollowUpResendDialog from './FollowUpResendDialog';
import { followUpActions, followUpCell } from './followUpCell';
import { ActionBar, Modal, RowMenu } from '../../components/fs';
import { COARSE, DESKTOP, PHONE, useMediaQuery } from '../../shared/media';
import { useToast } from '../../components/Toast';
import { downloadSubmissionsZip, saveBlob } from '../../api/forms';
import { usePageHeight } from './workflow/usePageHeight';
import {
  DELIVERY_INTENT, DeliveryState, TRIAGE_INTENT, TRIAGE_LABEL, deliveryPillText, formatFull, formatReceived, readColumn, triageOf,
} from './submissionColumns';

/** The two triage columns ride an export only when the checkbox says so (P5). */
const TRIAGE_COLUMN_IDS = ['meta:triageStatus', 'meta:assignee'];

const PAGE_SIZES = [25, 50, 100];

/**
 * Responses (R8, screens 18–19): the form's submissions as a table of its own
 * fields, with delivery health on every row, under a row of view chips.
 *
 * The two jobs this page exists for: "which submissions had a connection
 * error?" — answered by the Delivery column and the Needs attention chip — and
 * "find this company and see why its delivery failed", answered by search plus
 * the error text under the pill. The chips are filters, not places: New,
 * Needs attention, Mine, Nothing sent, Spam and Waiting on someone are each
 * one filter the URL can hold, so a saved view or a pasted link reproduces
 * any of them.
 */
/** The most submissions one zip of PDFs holds — the server's cap, mirrored. */
const PDF_ZIP_CAP = 200;
/**
 * The most rows one bulk action takes — the server's `BULK_CAP`, mirrored
 * (R0 found it was server-only). "Select all matching" stops here and says so.
 */
export const BULK_CAP = 200;

/** The chip conditions, spelt once so "is this chip on?" and "turn it on" agree. */
const NEW_CONDITION: FilterCondition = { column: 'meta:triageStatus', op: 'is', value: 'new' };
const SPAM_CONDITION: FilterCondition = { column: 'meta:triageStatus', op: 'is', value: 'spam' };
const WAITING_CONDITION: FilterCondition = { column: 'meta:status', op: 'is', value: 'awaiting' };
/** The Test chip (R9, D11): an editor's test fills, out of every other view the way spam is. */
const TEST_CONDITION: FilterCondition = { column: 'meta:isTest', op: 'is', value: 'true' };
const mineCondition = (email: string): FilterCondition => ({ column: 'meta:assignee', op: 'is', value: email });
const hasCondition = (f: SubmissionFilters, c: FilterCondition) =>
  (f.conditions || []).some((x) => x.column === c.column && x.op === c.op && String(x.value ?? '') === String(c.value ?? ''));

/**
 * Export as a split button: CSV is the default, the caret offers the rest.
 * Both are plain links to the export routes, carrying the table's filters.
 *
 * The one option lives in the caret menu: whether the triage columns (status
 * and assignee) go into the file. Off by default — they are the team's
 * working notes, not the respondent's answers — and the choice is the
 * browser's to remember for this session, nothing more.
 *
 * On a touch screen the caret's menu is RowMenu's action sheet — a scrim, the
 * menu's title, 48px rows (CLAUDE.md rules 3 and 5; R14 found the dropdown
 * below on phones). A mouse keeps the anchored dropdown exactly as it was.
 */
function ExportSplit({ hrefFor }: { hrefFor: (format: 'csv' | 'xlsx', triage: boolean) => string }) {
  const [open, setOpen] = useState(false);
  const [triage, setTriage] = useState(false);
  const coarse = useMediaQuery(COARSE);
  const csvHref = hrefFor('csv', triage);
  const xlsxHref = hrefFor('xlsx', triage);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open || coarse) return;
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open, coarse]);
  if (coarse) {
    return (
      <span className="fs-split">
        <a className="fs-btn neutral sm" href={csvHref}>Export CSV</a>
        <RowMenu
          open={open} onToggle={setOpen} label="Export"
          renderTrigger={({ open: isOpen, onClick, onKeyDown }) => (
            <button
              type="button" className={`fs-btn neutral sm ${isOpen ? 'open' : ''}`} aria-label="Other export formats"
              aria-haspopup="menu" aria-expanded={isOpen} onClick={onClick} onKeyDown={onKeyDown}
            >
              ▾
            </button>
          )}
        >
          <a role="menuitem" href={csvHref}>CSV</a>
          <a role="menuitem" href={xlsxHref}>Excel (.xlsx)</a>
          <div className="rule" />
          {/* ticking it keeps the sheet up, so a format can still be chosen */}
          <label className="fs-check fs-menu-check" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={triage} onChange={(e) => setTriage(e.target.checked)} />
            <span><b>Include triage columns</b><small>Status and assignee, at the end.</small></span>
          </label>
        </RowMenu>
      </span>
    );
  }
  return (
    <span className="fs-split" ref={ref}>
      <a className="fs-btn neutral sm" href={csvHref}>Export CSV</a>
      <span className="fs-menu-anchor">
        <button
          type="button"
          className={`fs-btn neutral sm ${open ? 'open' : ''}`}
          aria-label="Other export formats"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          ▾
        </button>
        {open && (
          <div className="fs-menu" role="menu" onClick={(e) => e.stopPropagation()}>
            <a role="menuitem" href={csvHref} onClick={() => setOpen(false)}>CSV</a>
            <a role="menuitem" href={xlsxHref} onClick={() => setOpen(false)}>Excel (.xlsx)</a>
            <div className="rule" />
            <label className="fs-check" style={{ padding: '6px 12px' }}>
              <input type="checkbox" checked={triage} onChange={(e) => setTriage(e.target.checked)} />
              <span><b>Include triage columns</b><small>Status and assignee, at the end.</small></span>
            </label>
          </div>
        )}
      </span>
    </span>
  );
}

/** The Test pill (R9, D11) beside a test fill's id — in the table, the phone card and the detail panel's head. */
export function TestPill() {
  return (
    <span className="fs-pill info sl-test-pill" title="A test from the builder's Test mode — saved as a test, outside your quota, delivered nowhere">
      Test
    </span>
  );
}

/** One view chip: a filter, pressed or not, with its count beside the word. */
function ViewChip({ on, onClick, count, title, children, className }: {
  on: boolean; onClick: () => void; count?: number | null; title?: string; children: React.ReactNode; className?: string;
}) {
  return (
    <button
      type="button" className={`sl-vchip${on ? ' on' : ''}${className ? ` ${className}` : ''}`}
      aria-pressed={on} title={title} onClick={onClick}
    >
      {children}{count != null && count > 0 && <> <span className="count">{count.toLocaleString()}</span></>}
    </button>
  );
}

export default function SubmissionsList() {
  const { form } = useFormWorkspace();
  const [params, setParams] = useSearchParams();
  // The drawer takes over the row's own URL, so existing deep links still work.
  const { submissionId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState<SubmissionRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [needsAttention, setNeedsAttention] = useState(0);
  /** how many submissions are still waiting on somebody (W3) */
  const [waiting, setWaiting] = useState(0);
  /** triage (P5): the bin's own count, and who a row can be handed to */
  const [spam, setSpam] = useState(0);
  /** the design's two chips (R8): triage `new`, and what is assigned to me */
  const [newCount, setNewCount] = useState(0);
  /** test fills (R9): the Test chip's own count */
  const [testCount, setTestCount] = useState(0);
  const [mineCount, setMineCount] = useState(0);
  const [assignees, setAssignees] = useState<AssigneeRef[]>([]);
  const [columns, setColumns] = useState<SubmissionColumn[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [defaults, setDefaults] = useState<string[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  /**
   * Rows "Select all matching" fetched beyond the page, so the bar can still
   * tell what it holds (spam or not) for rows the table is not showing.
   */
  const [fetchedRows, setFetchedRows] = useState<SubmissionRow[]>([]);
  const [selectingAll, setSelectingAll] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [views, setViews] = useState<SubmissionView[]>([]);
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const [connections, setConnections] = useState<{ id: number; name: string; type: string }[]>([]);
  const [me, setMe] = useState<{ id: number; email: string; role: string; masquerading: boolean } | null>(null);
  const [openPanel, setOpenPanel] = useState<'columns' | 'views' | 'filter' | null>(null);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const [bulkResend, setBulkResend] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [zipping, setZipping] = useState(false);
  /*
    F1' — the phone list. A 1000px table of user-picked columns becomes a card
    per submission, the four toolbar tools become one row of their own, and
    picking rows is a mode with a sticky bar rather than a permanent checkbox
    column. The desktop renders none of it (`phone` is false without a
    matching media query, jsdom included), so the table, the column whitelist,
    sort, pagination, export and the saved views are exactly as they were.
  */
  const phone = useMediaQuery(PHONE);
  /**
   * R8: on a desktop the detail panel is a locked column beside the table
   * (screen 19) and the page sizes itself to the viewport, so the list and
   * the panel scroll on their own; below 1100 the panel is the overlay drawer
   * it always was and the page scrolls.
   */
  const desktop = useMediaQuery(DESKTOP);
  const pageRef = useRef<HTMLDivElement>(null);
  usePageHeight(pageRef, desktop);
  const [selecting, setSelecting] = useState(false);
  const [bulkMenu, setBulkMenu] = useState(false);
  const toast = useToast();

  /** The selected rows' documents, zipped — the one export that follows the selection. */
  const downloadPdfs = useCallback(async () => {
    const ids = [...picked];
    setZipping(true);
    try {
      const blob = await downloadSubmissionsZip(form.id, ids);
      saveBlob(blob, `${form.slug}-submissions.zip`);
      toast(`${ids.length} PDF${ids.length === 1 ? '' : 's'} zipped.`);
    } catch (e) {
      toast((e as Error).message || 'The PDFs could not be produced.', { kind: 'error' });
    } finally {
      setZipping(false);
    }
  }, [form.id, form.slug, picked, toast]);

  // The whole view lives in the URL, so a pasted link reproduces it exactly.
  const filters: SubmissionFilters = useMemo(() => {
    let conditions: FilterCondition[] = [];
    const raw = params.get('conditions');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) conditions = parsed;
      } catch { /* a hand-mangled link simply carries no conditions */ }
    }
    return {
      q: params.get('q') || undefined,
      delivery: (params.get('delivery') as DeliveryFilter) || undefined,
      connectionId: params.get('connectionId') ? Number(params.get('connectionId')) : undefined,
      from: params.get('from') || undefined,
      to: params.get('to') || undefined,
      match: params.get('match') === 'any' ? 'any' : 'all',
      conditions,
    };
  }, [params]);
  const page = Math.max(1, Number(params.get('page')) || 1);
  const limit = PAGE_SIZES.includes(Number(params.get('limit'))) ? Number(params.get('limit')) : 25;
  const sort = params.get('sort') || 'meta:createdAt';
  const dir = params.get('dir') === 'asc' ? 'asc' : 'desc';

  /** Search is typed, so it is debounced separately from the URL it writes to. */
  const [search, setSearch] = useState(filters.q || '');
  useEffect(() => { setSearch(filters.q || ''); }, [filters.q]);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Replace every filter at once — used by Apply and by opening a view. */
  const applyFilters = useCallback((next: SubmissionFilters, keepView = false) => {
    const p = new URLSearchParams();
    const carry = ['sort', 'dir', 'limit'];
    for (const k of carry) { const v = params.get(k); if (v) p.set(k, v); }
    if (next.q) p.set('q', next.q);
    if (next.delivery) p.set('delivery', next.delivery);
    if (next.connectionId) p.set('connectionId', String(next.connectionId));
    if (next.from) p.set('from', next.from);
    if (next.to) p.set('to', next.to);
    if (next.match === 'any') p.set('match', 'any');
    const complete = (next.conditions || []).filter(conditionComplete);
    if (complete.length) p.set('conditions', JSON.stringify(complete));
    setParams(p, { replace: true });
    if (!keepView) setActiveViewId(null);
  }, [params, setParams]);

  const patch = useCallback((next: Record<string, string | number | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === '' || v === null) p.delete(k);
      else p.set(k, String(v));
    }
    // Any change to what is being looked at starts again at page one.
    if (!('page' in next)) p.delete('page');
    setParams(p, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    getSubmissionColumns(form.id)
      .then((r) => { setColumns(r.columns); setSelected(r.selected); setDefaults(r.defaults); })
      .catch((e) => setError(e.message));
    listViewsApi(form.id).then((r) => setViews(r.views)).catch(() => undefined);
    listFormConnections(form.id)
      .then((r) => setConnections(r.connections.map((c) => ({ id: c.id, name: c.name, type: c.type }))))
      .catch(() => undefined);
    adminMe()
      .then((r) => setMe({ id: r.admin.id, email: r.admin.email, role: r.admin.role, masquerading: !!r.masquerading }))
      .catch(() => undefined);
    listAssigneesApi(form.id).then((r) => setAssignees(r.users)).catch(() => setAssignees([]));
  }, [form.id]);

  const load = useCallback(() => {
    setBusy(true);
    const query = submissionQueryString({ ...filters, page, limit, sort, dir });
    querySubmissionsApi(form.id, query)
      .then((r) => {
        setRows(r.submissions);
        setTotal(r.total);
        setNeedsAttention(r.counts?.needsAttention ?? 0);
        setWaiting(r.counts?.waiting ?? 0);
        setSpam(r.counts?.spam ?? 0);
        setNewCount(r.counts?.new ?? 0);
        setMineCount(r.counts?.mine ?? 0);
        setTestCount(r.counts?.test ?? 0);
        setPicked(new Set());
        setFetchedRows([]);
        setError('');
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [form.id, filters, page, limit, sort, dir]);
  useEffect(() => { load(); }, [load]);

  /**
   * The live "Matches n" in the filter panel.
   *
   * Asks for a single row: the count is what matters and the rows are not shown
   * until Apply, so fetching a page of them would be waste.
   */
  const countMatches = useCallback((draft: SubmissionFilters) => {
    setCounting(true);
    querySubmissionsApi(form.id, submissionQueryString({ ...draft, limit: 1 }))
      .then((r) => setMatchCount(r.total))
      .catch(() => setMatchCount(null))
      .finally(() => setCounting(false));
  }, [form.id]);

  /**
   * "Select all {n} matching" (R8): every row the filters admit, up to the
   * server's cap, fetched as one page so the bar can act on rows the table is
   * not showing. Past the cap it takes the first BULK_CAP and says so.
   */
  const selectAllMatching = useCallback(async () => {
    setSelectingAll(true);
    try {
      const r = await querySubmissionsApi(form.id, submissionQueryString({ ...filters, page: 1, limit: BULK_CAP, sort, dir }));
      setFetchedRows(r.submissions);
      setPicked(new Set(r.submissions.map((s) => s.id)));
    } catch (e) {
      toast((e as Error).message || 'Could not select every matching row.', { kind: 'error' });
    } finally {
      setSelectingAll(false);
    }
  }, [form.id, filters, sort, dir, toast]);

  const persistColumns = useCallback((next: string[]) => {
    setSelected(next);
    saveSubmissionColumns(form.id, next).catch((e) => setError(e.message));
  }, [form.id]);

  const shown = useMemo(
    () => selected.map((id) => columns.find((c) => c.id === id)).filter(Boolean) as SubmissionColumn[],
    [selected, columns]
  );
  /** The first answer column — the row's "name", bold while the row is New (screen 18). */
  const leadColumn = useMemo(() => shown.find((c) => !c.id.startsWith('meta:'))?.id, [shown]);

  /**
   * Does this form hand somebody a link? The server only offers the Follow-up
   * column where it does, so its presence is the answer — and the row actions,
   * the chip and the ⋯ column all hang off the same fact rather than each
   * asking it a different way.
   */
  const hasFollowUp = useMemo(() => columns.some((c) => c.id === 'meta:status'), [columns]);
  const waitingOn = hasCondition(filters, WAITING_CONDITION);
  /**
   * The spam bin is a filter, not a place (P5): the default view leaves spam
   * out on the server, and "Spam (N)" is the one condition that brings it
   * back — the same shape as "Waiting on someone", so a saved view can hold
   * it and the chip row can take it off again.
   */
  const spamOn = hasCondition(filters, SPAM_CONDITION);
  /** Test fills (R9) are a filter the same way: out by default, one condition brings them back. */
  const testOn = hasCondition(filters, TEST_CONDITION);
  const newOn = hasCondition(filters, NEW_CONDITION);
  /**
   * "Mine" is assigned-to-me, as the assignee column's own condition (it
   * reads by email). In support mode there is no "me" on the account, so the
   * chip is not offered at all rather than offered empty.
   */
  const mine = me && !me.masquerading && me.email ? mineCondition(me.email) : null;
  const mineOn = !!mine && hasCondition(filters, mine);
  const chipOn = newOn || spamOn || testOn || waitingOn || mineOn || !!filters.delivery;
  /** the submission whose link is being re-sent, and how */
  const [resendInvite, setResendInvite] = useState<{ row: SubmissionRow; mode: 'same' | 'fresh' } | null>(null);
  const [copied, setCopied] = useState<number | null>(null);
  const [menuRow, setMenuRow] = useState<number | null>(null);

  /** A chip that is one condition: pressing it again takes it off. */
  const toggleCondition = (on: boolean, condition: FilterCondition) => {
    setActiveViewId(null);
    applyFilters(on ? {} : { conditions: [condition] });
  };

  async function onDeleteSelected() {
    setConfirmDelete(false);
    try {
      // One request for the whole selection (T4): the server deletes each
      // row whole — files included — and the trail records one count.
      await bulkDeleteSubmissionsApi(form.id, [...picked]);
      load();
    } catch (e: any) { setError(e.message); }
  }

  /** One triage write over some rows, then the table reloads — a row moved to spam leaves the default view. */
  const triageRows = useCallback(async (ids: number[], patch: TriagePatch) => {
    if (!ids.length) return;
    try {
      const r = await bulkTriageApi(form.id, ids, patch);
      const n = r.updated;
      const what = patch.status
        ? `Marked ${n} ${TRIAGE_LABEL[patch.status].toLowerCase()}.`
        : patch.assigneeUserId === null
          ? `Unassigned ${n}.`
          : `Assigned ${n} to ${assignees.find((u) => u.id === patch.assigneeUserId)?.name || 'them'}${r.emailed ? ' — they have been emailed.' : '.'}`;
      toast(what.replace(/^(Marked|Unassigned|Assigned) 1 /, (m) => m));
      load();
    } catch (e) {
      toast((e as Error).message || 'The change could not be saved.', { kind: 'error' });
    }
  }, [form.id, assignees, load, toast]);

  /** The picked rows — on the page or fetched by "Select all" — and whether every one of them is already in the bin. */
  const pickedRows = useMemo(() => {
    const byId = new Map<number, SubmissionRow>();
    for (const r of [...(rows || []), ...fetchedRows]) if (picked.has(r.id)) byId.set(r.id, r);
    return [...byId.values()];
  }, [rows, fetchedRows, picked]);
  const allPickedSpam = pickedRows.length > 0 && pickedRows.every((r) => triageOf(r) === 'spam');
  /** What a bulk re-send may actually send: spam stays put, however it was selected. */
  const resendIds = useMemo(() => pickedRows.filter((r) => triageOf(r) !== 'spam').map((r) => r.id), [pickedRows]);
  /** More rows match than are picked, and the cap leaves room: offer the rest. */
  const canSelectAll = total > picked.size && picked.size < BULK_CAP;
  const selectAllCount = Math.min(total, BULK_CAP);

  /**
   * Opening a view restores its filters, columns and sort together — that is
   * what a view IS. The column set is not written to the person's own
   * preference, so leaving the view puts their own choice back.
   */
  const openView = useCallback((v: SubmissionView) => {
    const p = new URLSearchParams();
    const f = v.config?.filters || {};
    if (f.q) p.set('q', f.q);
    if (f.delivery) p.set('delivery', f.delivery);
    if (f.connectionId) p.set('connectionId', String(f.connectionId));
    if (f.from) p.set('from', f.from);
    if (f.to) p.set('to', f.to);
    if (f.match === 'any') p.set('match', 'any');
    if (f.conditions?.length) p.set('conditions', JSON.stringify(f.conditions));
    if (v.config?.sort?.column) { p.set('sort', v.config.sort.column); p.set('dir', v.config.sort.dir); }
    setParams(p, { replace: true });
    if (v.config?.columns?.length) setSelected(v.config.columns);
    setActiveViewId(v.id);
  }, [setParams]);

  /** What a view would store right now: the table's filters, columns and sort. */
  const currentConfig = useCallback((): ViewConfig => ({ columns: selected, sort: { column: sort, dir }, filters }), [selected, sort, dir, filters]);

  const saveView = useCallback(async (name: string, shared: boolean) => {
    try {
      const r = await createViewApi(form.id, { name, shared, config: currentConfig() });
      setViews((list) => [...list, r.view]);
      setActiveViewId(r.view.id);
      setOpenPanel(null);
    } catch (e: any) { setError(e.message); }
  }, [form.id, currentConfig]);

  /**
   * Editing a view (R8; R0 found `updateViewApi` was never called): a new
   * name, a change of sharing, or the table's current filters, columns and
   * sort written over what it held. The server decides who may.
   */
  const updateView = useCallback(async (v: SubmissionView, change: { name?: string; shared?: boolean; useCurrent?: boolean }) => {
    try {
      const body: Record<string, unknown> = {};
      if (change.name !== undefined) body.name = change.name;
      if (change.shared !== undefined) body.shared = change.shared;
      if (change.useCurrent) body.config = currentConfig();
      const r = await updateViewApi(form.id, v.id, body);
      setViews((list) => list.map((x) => (x.id === v.id ? r.view : x)));
      toast(change.useCurrent ? `“${r.view.name}” now holds these filters.` : 'View saved.');
    } catch (e: any) {
      toast(e.message || 'The view could not be changed.', { kind: 'error' });
      throw e;
    }
  }, [form.id, currentConfig, toast]);

  const removeView = useCallback(async (v: SubmissionView) => {
    if (!window.confirm(`Delete the view “${v.name}”?`)) return;
    try {
      await deleteViewApi(form.id, v.id);
      setViews((list) => list.filter((x) => x.id !== v.id));
      if (activeViewId === v.id) setActiveViewId(null);
    } catch (e: any) { setError(e.message); }
  }, [form.id, activeViewId]);

  /** One chip per active filter, each knowing what removing it leaves behind. */
  const chips = useMemo(() => {
    const out: { key: string; kind: string; label: React.ReactNode; without: SubmissionFilters }[] = [];
    if (filters.delivery) {
      const labels: Record<string, string> = {
        any_failed: 'any failed', pending: 'pending', all_delivered: 'all delivered', none_sent: 'nothing sent',
      };
      const label = labels[filters.delivery] || filters.delivery;
      out.push({
        key: 'delivery', kind: 'delivery',
        label: <>Delivery: {label}</>,
        without: { ...filters, delivery: undefined, connectionId: undefined },
      });
    }
    if (filters.connectionId) {
      const conn = connections.find((c) => c.id === filters.connectionId);
      out.push({
        key: 'conn', kind: 'delivery',
        label: <>at <b>{conn?.name || `connection ${filters.connectionId}`}</b></>,
        without: { ...filters, connectionId: undefined },
      });
    }
    if (filters.from || filters.to) {
      out.push({
        key: 'date', kind: 'date',
        label: <>Received: {filters.from || '…'} → {filters.to || '…'}</>,
        without: { ...filters, from: undefined, to: undefined },
      });
    }
    for (const [i, c] of (filters.conditions || []).entries()) {
      const col = columns.find((x) => x.id === c.column);
      out.push({
        key: `c${i}`, kind: 'answer',
        label: <>{col?.label || c.column} {opLabel(c.op)} <b>{String(c.value ?? '')}</b></>,
        without: { ...filters, conditions: (filters.conditions || []).filter((_, j) => j !== i) },
      });
    }
    return out;
  }, [filters, columns, connections]);

  /** "3 of 12" — where this row sits in the filtered set, not in the page. */
  const drawerPosition = useMemo(() => {
    if (!submissionId || !rows) return null;
    const at = rows.findIndex((r) => r.id === Number(submissionId));
    return at < 0 ? null : { index: (page - 1) * limit + at + 1, total };
  }, [submissionId, rows, page, limit, total]);

  /** The table's own query string, for links that must keep the view. */
  const currentQuery = useMemo(() => {
    const p = new URLSearchParams(params);
    p.delete('edit');
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  }, [params]);

  const stripEdit = useCallback(() => {
    const p = new URLSearchParams(params);
    p.delete('edit');
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  }, [params]);

  /** Every connection on the form, for a bulk re-send that has no one row. */
  const bulkConnections = useMemo(
    () => connections.map((c) => ({
      id: c.id, name: c.name, type: c.type, enabled: true, dryRun: false,
      status: 'never' as const, lastAttemptAt: null, attempts: 0, error: null,
      configChangedAt: null, preselect: false,
    })),
    [connections]
  );

  /** The export follows what is on screen, filters and all. */
  const exportQuery = useMemo(() => {
    const qs = submissionQueryString({ ...filters, sort, dir });
    const cols = selected.length ? `columns=${encodeURIComponent(selected.join(','))}` : '';
    const all = [qs, cols].filter(Boolean).join('&');
    return all ? `?${all}` : '';
  }, [filters, sort, dir, selected]);

  /**
   * An export link. The server strips the triage columns unless `triage=1`
   * rides along, so the checkbox is a flag on the URL rather than a change to
   * the column list — the list stays "what the table shows".
   */
  const exportHref = useCallback((format: 'csv' | 'xlsx', triage: boolean) => {
    const flag = triage ? `${exportQuery ? '&' : '?'}triage=1` : '';
    return `/api/admin/forms/${form.id}/submissions/export.${format}${exportQuery}${flag}`;
  }, [form.id, exportQuery]);

  const pages = Math.max(1, Math.ceil(total / limit));
  const filtered = chips.length > 0 || !!filters.q;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const openId = submissionId ? Number(submissionId) : null;
  const detailHref = (id: number) => `/admin/forms/${form.id}/submissions/${id}${currentQuery}`;
  const docked = desktop && !!submissionId;

  /** The row's own controls keep their jobs; anywhere else on the row opens it (screen 19). */
  const onRowClick = (e: React.MouseEvent, id: number) => {
    if ((e.target as HTMLElement).closest('a, button, input, label, select, [role="menu"]')) return;
    navigate(detailHref(id));
  };

  const drawer = submissionId && (
    <SubmissionDrawer
      form={form}
      submissionId={Number(submissionId)}
      siblings={(rows || []).map((r) => r.id)}
      position={drawerPosition}
      editing={params.get('edit') === '1'}
      docked={docked}
      onNavigate={(id) => navigate(`/admin/forms/${form.id}/submissions/${id}${window.location.search}`, { replace: true })}
      onClose={() => navigate(`/admin/forms/${form.id}/submissions${stripEdit()}`)}
      onSetEditing={(on) => {
        const p = new URLSearchParams(params);
        if (on) p.set('edit', '1'); else p.delete('edit');
        setParams(p, { replace: true });
      }}
      onChanged={load}
    />
  );

  /** Where a bulk action comes from is the same three calls wherever the bar is drawn. */
  const bulk = {
    status: (status: TriageStatus) => void triageRows([...picked], { status }),
    assign: (assigneeUserId: number | null) => void triageRows([...picked], { assigneeUserId }),
  };

  return (
    <div className={`sl-page${docked ? ' with-panel' : ''}`} ref={pageRef}>
    <div className="sl-main">
      <div className="sl-toolbar">
        {/* ONE toolbar row: the view chips (filtering, not navigation),
            search, then the three neutral tools. */}
        <div className="sl-row">
          <div className="sl-views" role="group" aria-label="Views">
            <ViewChip
              on={!activeViewId && !chipOn}
              onClick={() => { setActiveViewId(null); applyFilters({}); }}
              count={total}
            >
              All
            </ViewChip>
            <ViewChip on={!activeViewId && newOn} count={newCount} onClick={() => toggleCondition(newOn, NEW_CONDITION)}>
              New
            </ViewChip>
            <ViewChip
              on={!activeViewId && filters.delivery === 'any_failed'} count={needsAttention}
              title="A connection failed on these — derived from the deliveries, not a status"
              onClick={() => { setActiveViewId(null); patch({ delivery: filters.delivery === 'any_failed' ? undefined : 'any_failed' }); }}
            >
              Needs attention
            </ViewChip>
            {mine && (
              <ViewChip on={!activeViewId && mineOn} count={mineCount} title="Assigned to you" onClick={() => toggleCondition(mineOn, mine)}>
                Mine
              </ViewChip>
            )}
            <ViewChip
              on={!activeViewId && filters.delivery === 'none_sent'}
              onClick={() => { setActiveViewId(null); patch({ delivery: filters.delivery === 'none_sent' ? undefined : 'none_sent' }); }}
            >
              Nothing sent
            </ViewChip>
            {/* The bin: only offered once there is something in it, or while looking at it. */}
            {(spam > 0 || spamOn) && (
              <ViewChip
                on={!activeViewId && spamOn} count={spam}
                title="Submissions marked as spam — left out of every other view, export and re-send"
                onClick={() => toggleCondition(spamOn, SPAM_CONDITION)}
              >
                Spam
              </ViewChip>
            )}
            {/* Test fills (R9, D11): offered once there is one, or while looking at them. */}
            {(testCount > 0 || testOn) && (
              <ViewChip
                on={!activeViewId && testOn} count={testCount}
                title="Filled in from the builder's Test mode — saved as tests, outside your quota, delivered nowhere, and left out of every other view, Summary and Analytics"
                onClick={() => toggleCondition(testOn, TEST_CONDITION)}
              >
                Test
              </ViewChip>
            )}
            {/* Only on a form that hands somebody a link. A built-in chip
                rather than a seeded saved view: the same filter, with nothing
                to migrate, nothing to seed per form, and nothing that can go
                stale when the form changes. */}
            {hasFollowUp && (
              <ViewChip on={!activeViewId && waitingOn} count={waiting} onClick={() => toggleCondition(waitingOn, WAITING_CONDITION)}>
                Waiting on someone
              </ViewChip>
            )}
            {views.map((v) => (
              <ViewChip
                key={v.id} on={activeViewId === v.id} className="saved"
                title={v.shared ? 'Shared with the team' : 'Only you can see this view'}
                onClick={() => openView(v)}
              >
                {v.name}{v.shared ? ' · shared' : ''}
              </ViewChip>
            ))}
            <span className="sl-anchor">
              <button
                type="button" className="sl-vchip add"
                aria-expanded={openPanel === 'views'}
                onClick={() => setOpenPanel(openPanel === 'views' ? null : 'views')}
              >
                + Save view
              </button>
              <Popover open={openPanel === 'views'} onClose={() => setOpenPanel(null)} width={400} align="left">
                <ViewsPanel
                  views={views}
                  activeId={activeViewId}
                  canEdit={(v) => !!me && (v.created_by === me.id || me.role === 'owner' || me.role === 'admin' || me.role === 'superadmin')}
                  onApply={(v) => { openView(v); setOpenPanel(null); }}
                  onSave={saveView}
                  onUpdate={updateView}
                  onDelete={removeView}
                />
              </Popover>
            </span>
          </div>
          <div className="sl-tools">
          <input
            className="fs-input fs-w-240 sl-search"
            placeholder="Search responses"
            aria-label="Search responses"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (searchTimer.current) clearTimeout(searchTimer.current);
              searchTimer.current = setTimeout(() => patch({ q: e.target.value || undefined }), 300);
            }}
          />
          <span className="sl-anchor">
            <button className="fs-btn neutral sm" onClick={() => setOpenPanel(openPanel === 'filter' ? null : 'filter')}>
              Filter
              {chips.length > 0 && <span className="sl-count-badge">{chips.length}</span>}
            </button>
            <Popover open={openPanel === 'filter'} onClose={() => setOpenPanel(null)} width={600}>
              <FilterPanel
                columns={columns}
                filters={filters}
                connections={connections}
                matches={matchCount}
                counting={counting}
                onCount={countMatches}
                onApply={(next) => { applyFilters(next); setOpenPanel(null); }}
                onClear={() => { applyFilters({}); setOpenPanel(null); }}
              />
            </Popover>
          </span>
          <span className="sl-anchor">
            <button className="fs-btn neutral sm" onClick={() => setOpenPanel(openPanel === 'columns' ? null : 'columns')}>
              Columns
            </button>
            <Popover open={openPanel === 'columns'} onClose={() => setOpenPanel(null)} width={420}>
              <ColumnsPanel
                columns={columns}
                selected={selected}
                onChange={persistColumns}
                onReset={() => persistColumns(defaults)}
                onDone={() => setOpenPanel(null)}
              />
            </Popover>
          </span>
          <ExportSplit hrefFor={exportHref} />
          {phone && (
            <button
              type="button" className={`fs-btn neutral sm sl-select${selecting ? ' on' : ''}`}
              aria-pressed={selecting}
              onClick={() => { setSelecting((v) => !v); if (selecting) setPicked(new Set()); }}
            >
              {selecting ? 'Done' : 'Select'}
            </button>
          )}
          </div>
        </div>

        {chips.length > 0 && (
          <div className="sl-chips">
            <span className="sl-overline">Filters</span>
            {chips.map((chip) => (
              <span className={`sl-chip ${chip.kind}`} key={chip.key}>
                {chip.label}
                <button title="Remove this filter" onClick={() => applyFilters(chip.without)}>×</button>
              </span>
            ))}
            {filters.q && (
              <span className="sl-chips-note">
                Search <b>“{filters.q}”</b> matches any answer
              </span>
            )}
            <span style={{ flex: 1 }} />
            <button type="button" className="fs-btn text sm" onClick={() => setOpenPanel('views')}>
              Save as view
            </button>
            <button type="button" className="fs-btn text sm muted" onClick={() => applyFilters({})}>
              Clear all
            </button>
          </div>
        )}
      </div>

      {error && <div className="ad-err">{error}</div>}

      <div className="fs-card sl-card-table">
        {phone ? (
          <div className="sl-cards">
            {rows?.map((row) => (
              <SubmissionCard
                key={row.id}
                row={row} columns={shown} formId={form.id} query={currentQuery} hasFollowUp={hasFollowUp}
                selecting={selecting} picked={picked.has(row.id)}
                onPick={(on) => {
                  const next = new Set(picked);
                  if (on) next.add(row.id); else next.delete(row.id);
                  setPicked(next);
                }}
                menuOpen={menuRow === row.id}
                onMenu={(o) => setMenuRow(o ? row.id : null)}
                copied={copied === row.id}
                onCopy={() => {
                  const url = row.follow_up?.invite?.url;
                  if (!url) return;
                  navigator.clipboard?.writeText(url).then(
                    () => { setCopied(row.id); window.setTimeout(() => setCopied(null), 2000); },
                    () => setError('Could not copy the link to the clipboard.')
                  );
                }}
                onResend={(mode) => setResendInvite({ row, mode })}
                onTriage={(status) => void triageRows([row.id], { status })}
                onDelete={() => { setPicked(new Set([row.id])); setConfirmDelete(true); }}
              />
            ))}
          </div>
        ) : (
        <div className="sl-scroll">
          <table className="sl-table">
            <thead>
              <tr>
                <th className="sl-check">
                  <input
                    type="checkbox"
                    aria-label="Select every row on this page"
                    checked={!!rows?.length && rows.every((r) => picked.has(r.id))}
                    onChange={(e) => {
                      const next = new Set(picked);
                      for (const r of rows || []) { if (e.target.checked) next.add(r.id); else next.delete(r.id); }
                      setPicked(next);
                    }}
                  />
                </th>
                {shown.map((c) => (
                  <th
                    key={c.id}
                    className={`sortable ${c.numeric ? 'num' : ''}`}
                    aria-sort={sort === c.id ? (dir === 'desc' ? 'descending' : 'ascending') : undefined}
                    onClick={() => patch({ sort: c.id, dir: sort === c.id && dir === 'desc' ? 'asc' : 'desc' })}
                  >
                    <span className="sl-th-label" title={c.label}>{c.label}</span>
                    {sort === c.id && <span className="sl-sort" aria-hidden="true">{dir === 'desc' ? '↓' : '↑'}</span>}
                  </th>
                ))}
                <th className="sl-actions" />
              </tr>
            </thead>
            <tbody>
              {rows?.map((row) => (
                <tr
                  key={row.id}
                  className={`${picked.has(row.id) ? 'on' : ''} ${openId === row.id ? 'open' : ''} ${triageOf(row) === 'new' ? 'is-new' : ''}`.trim()}
                  aria-selected={openId === row.id || undefined}
                  onClick={(e) => onRowClick(e, row.id)}
                >
                  <td className="sl-check">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.submission_id}`}
                      checked={picked.has(row.id)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(row.id); else next.delete(row.id);
                        setPicked(next);
                      }}
                    />
                  </td>
                  {shown.map((c) => (
                    <Cell
                      key={c.id} row={row} column={c} formId={form.id} search={filters.q} query={currentQuery}
                      lead={c.id === leadColumn}
                    />
                  ))}
                  <td className="sl-actions">
                    <RowActions
                      row={row} formId={form.id} query={currentQuery} hasFollowUp={hasFollowUp}
                      open={menuRow === row.id} onToggle={(o) => setMenuRow(o ? row.id : null)}
                      copied={copied === row.id}
                      onCopy={() => {
                        const url = row.follow_up?.invite?.url;
                        if (!url) return;
                        navigator.clipboard?.writeText(url).then(
                          () => { setCopied(row.id); window.setTimeout(() => setCopied(null), 2000); },
                          () => setError('Could not copy the link to the clipboard.')
                        );
                      }}
                      onResend={(mode) => setResendInvite({ row, mode })}
                      onTriage={(status) => void triageRows([row.id], { status })}
                      onDelete={() => { setPicked(new Set([row.id])); setConfirmDelete(true); }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {rows && rows.length === 0 && (
          <div className="sl-empty">
            {filtered ? (
              <>
                No responses match these filters.{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); applyFilters({}); }}>Clear all</a>
              </>
            ) : 'No responses in this view.'}
          </div>
        )}
        {!rows && <div className="sl-empty">Loading…</div>}

        {/* The footer is the count and the pager; the bulk actions are the
            ink bar below, only while rows are selected. */}
        <div className="sl-foot">
          <span>
            {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()} matching
            {needsAttention > 0 && ` · ${needsAttention} need attention`}
          </span>
          <span style={{ flex: 1 }} />
          <label className="sl-rows">
            Rows
            <select aria-label="Rows per page" value={limit} onChange={(e) => patch({ limit: Number(e.target.value) })}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="fs-btn neutral sm" disabled={page <= 1 || busy} onClick={() => patch({ page: page - 1 })}>← Prev</button>
          <span>Page {page} of {pages}</span>
          <button className="fs-btn neutral sm" disabled={page >= pages || busy} onClick={() => patch({ page: page + 1 })}>Next →</button>
        </div>
      </div>

      {/*
        The bulk bar (R8, screen 18): ink, sticky at the bottom, up as soon as
        a row is checked on a desktop and in Select mode on a phone (F11).
        The desktop carries the design's buttons; the phone keeps Status and
        Assign as its two everyday selects and the rest one tap further into
        the action sheet. Every one calls what the footer's buttons called.
      */}
      {picked.size > 0 && (!phone || selecting) && (
        <ActionBar className="ink sl-bulk">
          <b>{picked.size.toLocaleString()} selected</b>
          {canSelectAll && (
            <button
              type="button" className="fs-btn text sm sl-select-all" disabled={selectingAll}
              title={total > BULK_CAP ? `Bulk actions take ${BULK_CAP} at a time — this selects the first ${BULK_CAP} in the current order.` : undefined}
              onClick={() => void selectAllMatching()}
            >
              {selectingAll ? 'Selecting…' : total > BULK_CAP ? `Select the first ${BULK_CAP} matching` : `Select all ${selectAllCount.toLocaleString()} matching`}
            </button>
          )}
          {phone ? (
            <>
              <select
                className="fs-input" aria-label="Set status" value=""
                onChange={(e) => { if (e.target.value) bulk.status(e.target.value as TriageStatus); }}
              >
                <option value="">Status…</option>
                {TRIAGE_STATUSES.filter((s) => s !== 'spam').map((s) => <option key={s} value={s}>{TRIAGE_LABEL[s]}</option>)}
              </select>
              <select
                className="fs-input" aria-label="Assign to" value=""
                onChange={(e) => {
                  if (e.target.value === 'none') bulk.assign(null);
                  else if (e.target.value) bulk.assign(Number(e.target.value));
                }}
              >
                <option value="">Assign…</option>
                {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                <option value="none">Nobody</option>
              </select>
            </>
          ) : (
            <>
              <span className="sl-bulk-gap" />
              {mine && <button className="fs-btn neutral sm" onClick={() => bulk.assign(me!.id)}>Assign to me</button>}
              <button className="fs-btn neutral sm" onClick={() => bulk.status('in_review')}>Mark in review</button>
              <button className="fs-btn neutral sm" onClick={() => bulk.status('done')}>Mark done</button>
              {allPickedSpam
                ? <button className="fs-btn neutral sm" onClick={() => bulk.status('new')}>Not spam</button>
                : <button className="fs-btn neutral sm" onClick={() => bulk.status('spam')}>Mark spam</button>}
              <button
                className="fs-btn neutral sm"
                disabled={!resendIds.length}
                title={resendIds.length < picked.size ? `${picked.size - resendIds.length} marked as spam will be left out.` : undefined}
                onClick={() => setBulkResend(true)}
              >
                Re-send…
              </button>
            </>
          )}
          <RowMenu open={bulkMenu} onToggle={setBulkMenu} label={`${picked.size} selected`}>
            {phone && (allPickedSpam
              ? <button onClick={() => bulk.status('new')}>Not spam</button>
              : <button onClick={() => bulk.status('spam')}>Mark as spam</button>)}
            {phone && <button disabled={!resendIds.length} onClick={() => setBulkResend(true)}>Re-send to connections…</button>}
            {!phone && (
              <>
                <button onClick={() => bulk.status('new')}>Mark new</button>
                {assignees.map((u) => <button key={u.id} onClick={() => bulk.assign(u.id)}>Assign to {u.name}</button>)}
                <button onClick={() => bulk.assign(null)}>Unassign</button>
                <div className="rule" />
              </>
            )}
            {/* The ticked rows, by id. It used to send only the filters, and exported everything they matched. */}
            <a href={`/api/admin/forms/${form.id}/submissions/export.csv${exportQuery}${exportQuery ? '&' : '?'}ids=${[...picked].join(',')}`}>Export selected</a>
            <button
              disabled={zipping || picked.size > PDF_ZIP_CAP}
              title={picked.size > PDF_ZIP_CAP ? `PDFs download ${PDF_ZIP_CAP} at a time — narrow the selection.` : undefined}
              onClick={downloadPdfs}
            >
              {zipping ? 'Zipping…' : 'Download PDFs'}
            </button>
            <button onClick={() => setPicked(new Set())}>Clear the selection</button>
            {phone && (
              <>
                <div className="rule" />
                <button className="danger" onClick={() => setConfirmDelete(true)}>Delete…</button>
              </>
            )}
          </RowMenu>
          {!phone && (
            <>
              <button className="fs-btn neutral sm sl-bulk-danger" onClick={() => setConfirmDelete(true)}>Delete…</button>
              <button className="fs-btn icon sl-bulk-clear" aria-label="Clear the selection" title="Clear the selection" onClick={() => setPicked(new Set())}>✕</button>
            </>
          )}
        </ActionBar>
      )}

      {confirmDelete && (
        <Modal title={`Delete ${picked.size} response${picked.size === 1 ? '' : 's'}`} narrow
          onClose={() => setConfirmDelete(false)}
          footer={(
            <>
              <button className="fs-btn danger" onClick={onDeleteSelected}>
                Delete {picked.size === 1 ? 'it' : `all ${picked.size}`}
              </button>
              <button className="fs-btn neutral" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </>
          )}>
          <div className="fs-consequence danger">
            This permanently deletes {picked.size === 1 ? 'the selected response' : `the ${picked.size} selected responses`},
            including {picked.size === 1 ? 'its' : 'their'} delivery history. It cannot be undone.
          </div>
        </Modal>
      )}

      {!docked && drawer}

      {bulkResend && (
        <ResendDialog
          submissionId={0}
          submissionRef={resendIds.length < picked.size
            ? `${resendIds.length} submissions (${picked.size - resendIds.length} marked as spam left out)`
            : `${resendIds.length} submissions`}
          connections={bulkConnections}
          onClose={() => setBulkResend(false)}
          onSent={load}
          bulk={{ formId: form.id, submissionIds: resendIds }}
        />
      )}

      {resendInvite?.row.follow_up && (
        <FollowUpResendDialog
          submissionId={resendInvite.row.id}
          submissionRef={resendInvite.row.submission_id}
          followUp={resendInvite.row.follow_up}
          initial={resendInvite.mode}
          onClose={() => setResendInvite(null)}
          onSent={() => { setResendInvite(null); load(); }}
        />
      )}
    </div>
    {docked && <aside className="sl-side" aria-label="The open response">{drawer}</aside>}
    </div>
  );
}

/**
 * One submission, as a card (F1').
 *
 * Three lines answering the three questions the table's columns answer: which
 * one is this and where has triage put it, what is in it, and did what was
 * supposed to leave it actually leave. Line 2 is the first two columns the
 * person chose in the Columns sheet, so what a card shows is still theirs to
 * decide — the meta columns are left out of it because lines 1 and 3 already
 * carry them.
 */
function SubmissionCard({
  row, columns, formId, query, hasFollowUp, selecting, picked, onPick, menuOpen, onMenu,
  copied, onCopy, onResend, onTriage, onDelete,
}: {
  row: SubmissionRow;
  columns: SubmissionColumn[];
  formId: number;
  query: string;
  hasFollowUp: boolean;
  selecting: boolean;
  picked: boolean;
  onPick: (on: boolean) => void;
  menuOpen: boolean;
  onMenu: (open: boolean) => void;
  copied: boolean;
  onCopy: () => void;
  onResend: (mode: 'same' | 'fresh') => void;
  onTriage: (status: TriageStatus) => void;
  onDelete: () => void;
}) {
  const status = triageOf(row);
  const fields = columns.filter((c) => !c.id.startsWith('meta:')).slice(0, 2);
  const summary = row.delivery_summary;
  const answers = fields
    .map((c) => readColumn(row, c).text)
    .filter((t) => t && t !== '—')
    .join(' · ');

  return (
    <div className={`sl-card${picked ? ' on' : ''}`}>
      {selecting && (
        <label className="sl-card-pick">
          <input
            type="checkbox" checked={picked} aria-label={`Select ${row.submission_id}`}
            onChange={(e) => onPick(e.target.checked)}
          />
        </label>
      )}
      <div className="sl-card-body">
        <div className="sl-card-top">
          <Link className="sl-id" to={`/admin/forms/${formId}/submissions/${row.id}${query}`}>{row.submission_id}</Link>
          {(row.last_revision || 1) > 1 && <span className="sl-edited" title="Edited after it was submitted">edited</span>}
          {row.is_test && <TestPill />}
          <span className="sl-card-when">{new Date(row.created_at).toLocaleDateString()}</span>
          <span className={`fs-pill ${TRIAGE_INTENT[status]}`}>{TRIAGE_LABEL[status]}</span>
        </div>
        {answers && <div className="sl-card-answers">{answers}</div>}
        <div className="sl-card-deliv">
          {summary
            ? <span className={`fs-pill ${DELIVERY_INTENT[summary.state as DeliveryState]}`}>
              {deliveryPillText({ ...summary, state: summary.state as DeliveryState })}
            </span>
            : <span className="fs-pill neutral">nothing sent</span>}
          {summary?.firstError && (
            <span className="sl-sub bad">{summary.firstError.connectionName} · {summary.firstError.message}</span>
          )}
          {row.document_error && <span className="sl-sub bad">{row.document_error} — open it to retry</span>}
        </div>
      </div>
      <RowActions
        row={row} formId={formId} query={query} hasFollowUp={hasFollowUp}
        open={menuOpen} onToggle={onMenu} copied={copied}
        onCopy={onCopy} onResend={onResend} onTriage={onTriage} onDelete={onDelete}
      />
    </div>
  );
}

/**
 * The row's ⋯ menu: triage on every row, the follow-up actions where the form
 * sends a link.
 *
 * The follow-up entries are only the ones that mean something right now:
 * there is no "copy the link" for a submission whose link has been spent, and
 * "send it to them after all" only appears where a condition really did skip
 * the send — offering it everywhere would invite somebody to email a customer
 * the form's author deliberately decided not to email.
 */
function RowActions({ row, formId, query, hasFollowUp, open, onToggle, copied, onCopy, onResend, onTriage, onDelete }: {
  row: SubmissionRow; formId: number; query: string; hasFollowUp: boolean;
  open: boolean; onToggle: (open: boolean) => void;
  copied: boolean;
  onCopy: () => void;
  onResend: (mode: 'same' | 'fresh') => void;
  onTriage: (status: TriageStatus) => void;
  onDelete: () => void;
}) {
  const can = hasFollowUp ? followUpActions(row.follow_up) : null;
  const url = row.follow_up?.invite?.url;
  const status = triageOf(row);
  return (
    <RowMenu open={open} onToggle={onToggle}>
      <Link to={`/admin/forms/${formId}/submissions/${row.id}${query}`}>Open submission</Link>
      {can?.openChild && (
        <Link to={`/admin/forms/${row.follow_up!.child!.formId}/submissions/${row.follow_up!.child!.id}`}>
          Open the follow-up
        </Link>
      )}
      {can?.copyLink && url && (
        <button onClick={onCopy}>{copied ? 'Copied' : 'Copy follow-up link'}</button>
      )}
      {can?.resendSame && <button onClick={() => onResend('same')}>Re-send the link…</button>}
      {can?.resendFresh && <button onClick={() => onResend('fresh')}>Re-send with a fresh link…</button>}
      {can?.sendAfterAll && <button onClick={() => onResend('same')}>Send it to them after all…</button>}
      <div className="rule" />
      {status === 'spam'
        ? <button onClick={() => onTriage('new')}>Not spam</button>
        : (
          <>
            {status !== 'in_review' && <button onClick={() => onTriage('in_review')}>Mark in review</button>}
            {status !== 'done' && <button onClick={() => onTriage('done')}>Mark done</button>}
            <button onClick={() => onTriage('spam')}>Mark as spam</button>
          </>
        )}
      <div className="rule" />
      <button className="danger" onClick={onDelete}>Delete…</button>
    </RowMenu>
  );
}

function Cell({ row, column, formId, search, query, lead }: {
  row: SubmissionRow; column: SubmissionColumn; formId: number; search?: string; query: string;
  /** the first answer column: bold while the row is New (screen 18) */
  lead?: boolean;
}) {
  if (column.id === 'meta:submissionId') {
    return (
      <td>
        {/* Carries the current filters so the drawer opens over THIS set and
            ↑/↓ step through the same rows the table is showing. */}
        <Link className="sl-id" to={`/admin/forms/${formId}/submissions/${row.id}${query}`}>{row.submission_id}</Link>
        {(row.last_revision || 1) > 1 && <span className="sl-edited" title="Edited after it was submitted">edited</span>}
        {row.is_test && <TestPill />}
      </td>
    );
  }
  if (column.id === 'meta:deliveries') return <DeliveriesCell row={row} />;
  if (column.id === 'meta:status') return <FollowUpCell row={row} formId={formId} query={query} />;
  if (column.id === 'meta:triageStatus') {
    const s = triageOf(row);
    return <td><span className={`fs-pill ${TRIAGE_INTENT[s]}`}>{TRIAGE_LABEL[s]}</span></td>;
  }
  if (column.id === 'meta:createdAt') {
    return <td className="sl-when" title={formatFull(row.created_at)}>{formatReceived(row.created_at)}</td>;
  }
  if (column.id === 'meta:assignee') {
    const a = row.assignee;
    if (!a) return <td className="sl-none">—</td>;
    return (
      <td>
        <span className="sl-assignee" title={a.email}>
          <span className={`fs-avatar fs-pill ${avatarIntent(a.name)}`}>{initialsOf(a.name)}</span>
          {a.name}
        </span>
      </td>
    );
  }

  const { text, numeric } = readColumn(row, column);
  return (
    <td className={`${numeric ? 'num' : ''}${lead && triageOf(row) === 'new' ? ' sl-lead' : ''}`.trim() || undefined}>
      {numeric ? text : <Marked text={text} term={search} />}
    </td>
  );
}

/**
 * The column that did not exist before: one pill per row summarising every
 * delivery, plus the first failure's own words underneath — so the cause is
 * readable without opening anything.
 */
function DeliveriesCell({ row }: { row: SubmissionRow }) {
  const s = row.delivery_summary;
  if (!s) {
    return (
      <td>
        {row.document_error
          ? <span className="fs-pill danger" title={row.document_error}>check it</span>
          : '—'}
      </td>
    );
  }
  const state = s.state as DeliveryState;

  const sub = s.firstError
    ? { text: `${s.firstError.connectionName} · ${s.firstError.message}`, bad: true }
    : s.lastResendAt
      ? { text: `re-sent ${new Date(s.lastResendAt).toLocaleDateString()}${s.lastResendBy ? ` by ${s.lastResendBy}` : ''}`, bad: false }
      : s.state === 'partial' && s.skipped
        ? { text: `${s.skipped} in test mode at submit time`, bad: false }
        : null;

  return (
    <td>
      <span className={`fs-pill ${DELIVERY_INTENT[state]}`}>{deliveryPillText({ ...s, state })}</span>
      {sub && <div className={`sl-sub ${sub.bad ? 'bad' : ''}`} title={sub.text}>{sub.text}</div>}
      {/*
        A document that could not be kept rides this column rather than getting
        one of its own: it is the same question the deliveries pill answers —
        "did what was supposed to leave this submission actually leave it" —
        and a whole column that is blank on every healthy row is worse than a
        second line on the few rows that are not.
      */}
      {row.document_error && (
        <div className="sl-sub bad" title={row.document_error}>{row.document_error} — open it to retry</div>
      )}
    </td>
  );
}

/**
 * Where the follow-up got to, and the one fact that explains it.
 *
 * The pill on its own is not actionable — "awaiting" is fine on a link sent
 * this morning and a problem on one sent three weeks ago — so the sentence
 * under it carries who it went to, whether anybody opened it, and what the
 * follow-up recorded when it arrived.
 */
function FollowUpCell({ row, formId, query }: { row: SubmissionRow; formId: number; query: string }) {
  const cell = followUpCell(row.follow_up);
  if (!cell) return <td>—</td>;
  return (
    <td>
      <span className={`fs-pill ${cell.pill.intent}`}>{cell.pill.text}</span>
      <div
        className={`sl-sub ${cell.bad ? 'bad' : ''} ${cell.warn ? 'warn' : ''}`}
        title={`${cell.sub}${cell.child ? ` ${cell.child.submissionId}` : ''}${cell.after || ''}`}
      >
        {cell.sub}
        {cell.child && (
          <Link className="sl-id" to={`/admin/forms/${cell.child.formId}/submissions/${cell.child.id}`}>
            {cell.child.submissionId} →
          </Link>
        )}
        {cell.after}
      </div>
    </td>
  );
}

/** Highlights the search term inside the cell that matched it. */
function Marked({ text, term }: { text: string; term?: string }) {
  if (!term?.trim() || !text) return <>{text}</>;
  const i = text.toLowerCase().indexOf(term.trim().toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="sl-mark">{text.slice(i, i + term.trim().length)}</span>
      {text.slice(i + term.trim().length)}
    </>
  );
}
